import { useState } from 'react';

// Plain-language wiki for the employee portal — written for the crew, not techies.
interface Article { icon: string; title: string; body: React.ReactNode }

const A = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
);
const B = ({ children }: { children: React.ReactNode }) => (
  <strong className="text-gray-800">{children}</strong>
);

const ARTICLES: Article[] = [
  {
    icon: 'home',
    title: 'Home — your day at a glance',
    body: (
      <div className="space-y-2">
        <A>The Home screen shows your hours for the week and month, your jobs, and anything waiting for approval. The <B>Owed to you</B> banner shows money earned that hasn't been paid out yet.</A>
        <A><B>Today</B> lists where you're working today — tap the address to open directions in Maps. <B>Coming up</B> shows your next scheduled days.</A>
        <A>Tap the <B>My jobs</B> tile to see each job's details, tasks, and safety notes.</A>
      </div>
    ),
  },
  {
    icon: 'calendar_month',
    title: 'Schedule — where and when you work',
    body: (
      <div className="space-y-2">
        <A>The calendar shows your work days. A <B>solid orange tag</B> means a day the office scheduled you specifically. A <B>light orange tag</B> means the job is running during that stretch.</A>
        <A><B>Tap any day</B> to see the details below the calendar — job, times, address (tap for directions), and the task list for that day.</A>
        <A><B>Month timeline</B> at the bottom shows each job as a bar across the month — handy for seeing how jobs overlap. Swipe it sideways.</A>
      </div>
    ),
  },
  {
    icon: 'schedule',
    title: 'Hours — logging your time',
    body: (
      <div className="space-y-2">
        <A>Pick the day on the small calendar (days you've already logged show green totals). Enter your <B>start and end time</B> — the portal does the math, including the 30-minute lunch if the toggle is on.</A>
        <A>Worked at <B>two sites in one day?</B> Tap <B>+ Job</B> and give each site its own hours.</A>
        <A>Write a quick note in <B>Tasks completed</B> — what you got done that day. It's required before submitting.</A>
        <A><B>Save Draft</B> keeps it for later; <B>Submit</B> sends it to the office for approval. Made a mistake on a draft? Swipe it left to delete. Submitted and approved entries are locked — talk to the office to change those.</A>
        <A><B>No signal on site?</B> Log and submit as normal — if you're out of coverage the entry saves right on your phone (you'll see an amber "saved offline" note) and sends itself automatically the moment you're back in signal. Nothing is lost in dead zones.</A>
      </div>
    ),
  },
  {
    icon: 'account_balance_wallet',
    title: 'Financial — your money',
    body: (
      <div className="space-y-2">
        <A>The big number is what you're <B>owed</B>: approved hours × your rate, minus what's been paid to you. Submitted hours count only after the office approves them.</A>
        <A><B>Payments received</B> lists every payment with the date and how it was sent (e-Transfer, cheque, cash). <B>Hours worked</B> shows your time totals, and <B>Paystubs</B> holds formal paystubs when payroll runs.</A>
        <A>If your numbers look wrong, check whether hours are still <B>pending approval</B> first — then ask the office.</A>
      </div>
    ),
  },
  {
    icon: 'folder',
    title: 'Docs — plans, permits, photos',
    body: (
      <div className="space-y-2">
        <A>Each <B>folder is a job</B>; the files inside are what the office shared with you. Tap a file to open it right here — photos, PDFs, and even <B>3D truss models</B> (spin them with one finger, pinch to zoom; the viewer has its own help screen).</A>
        <A>Use the buttons in the viewer to <B>download</B> a file or <B>open it in a new tab</B>.</A>
        <A><B>Upload Job Photo</B> sends a site photo straight to the job's records — great for progress shots or documenting an issue.</A>
      </div>
    ),
  },
  {
    icon: 'manage_accounts',
    title: 'Account — password & notifications',
    body: (
      <div className="space-y-2">
        <A>Update your name and phone, change your password, and open <B>Notification settings</B> to choose email/text alerts (schedule changes, new documents, hours reminders).</A>
        <A><B>Forgot your password?</B> Ask the office to send you a reset link — it arrives by email and lets you set a new one.</A>
      </div>
    ),
  },
  {
    icon: 'calculate',
    title: 'Calculator — feet, inches & sixteenths',
    body: (
      <div className="space-y-2">
        <A>Open it from the <B>calculator button</B> on the Home screen. Every measurement is entered as <B>feet / inches / sixteenths</B> — and results come back the same way, plus decimal and metric.</A>
        <A><B>Add</B> joins or subtracts two measurements. <B>Square</B> gives the diagonal from two sides — for squaring foundations or rafter lengths. <B>Stairs</B> takes the total rise and gives you the riser count and exact riser height under the code max. <B>× / ÷</B> is for layouts (14 spaces × 16") or splitting a run into equal bays. <B>Convert</B> switches to decimal or millimetres.</A>
      </div>
    ),
  },
  {
    icon: 'menu_book',
    title: 'BC Building Code — quick lookups',
    body: (
      <div className="space-y-2">
        <A>Open it from the card at the top of <B>Docs</B>. Type what you're after — "stairs", "guard", "egress" — and the <B>quick-reference cards</B> show the common values with their code section numbers.</A>
        <A>The full code PDF is beside it: jump to a page, and <B>bookmark</B> pages you use a lot (bookmarks stay on your phone). Quick-ref values are a starting point — <B>always confirm in the code text</B>.</A>
      </div>
    ),
  },
  {
    icon: 'flag',
    title: 'Wrong hours on a locked entry?',
    body: (
      <div className="space-y-2">
        <A>Submitted and approved entries are locked — but if one is wrong, <B>swipe it left and tap Dispute</B>, then say what's off ("worked 8h not 6h"). The office sees a red flag on that entry and can correct it.</A>
      </div>
    ),
  },
  {
    icon: 'install_mobile',
    title: 'Install it like an app',
    body: (
      <div className="space-y-2">
        <A>Add OchrePoint to your home screen so it opens like a normal app — full screen, one tap, and it still works with no signal.</A>
        <A><B>iPhone (Safari):</B> tap the Share button → <B>Add to Home Screen</B>. <B>Android (Chrome):</B> tap the <B>⋮</B> menu → <B>Install app</B> / <B>Add to Home screen</B>.</A>
        <A>Always open it from that home-screen icon after installing. If it ever opens to a blank screen, it just needs a fresh copy — open it once more, or remove the icon and add it again.</A>
      </div>
    ),
  },
  {
    icon: 'help',
    title: 'Something not working?',
    body: (
      <div className="space-y-2">
        <A>First try <B>pulling down to refresh</B> the screen, or closing and reopening the portal. If the app opens blank from your home-screen icon, open it once more (it self-repairs) or remove and re-add the icon. Still stuck? Tell the office what screen you were on and what you expected to happen.</A>
      </div>
    ),
  },
];

export const PortalHelpPage = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-4 pb-4">
      <div>
        <h1 className="text-xl font-bold text-[#2C1810]">Help</h1>
        <p className="text-sm text-gray-500 mt-0.5">How to use your OchrePoint portal.</p>
      </div>
      <div className="space-y-2">
        {ARTICLES.map((a, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50"
            >
              <span className="material-symbols-outlined text-[#C77D3F] text-xl">{a.icon}</span>
              <span className="flex-1 font-semibold text-gray-900 text-sm">{a.title}</span>
              <span className={`material-symbols-outlined text-gray-400 transition-transform ${open === i ? 'rotate-180' : ''}`}>expand_more</span>
            </button>
            {open === i && <div className="px-4 pb-4 border-t border-gray-100 pt-3">{a.body}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};
